import { useState } from 'react';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useScheduledJobsQuery } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { AsyncButton } from '@/components/ui/async-button';
import { Icon, IconName } from '@/icons';
import { JobType } from '@/types/enums/JobType';
import { JobRunStatus } from '@/types/enums/JobRunStatus';
import type {
  RunScheduledJobInput,
  RunScheduledJobOutput,
  GetScheduledJobsOutput,
  GetScheduledJobsJobsItem,
} from '@/types/generated';

export interface RunJobDialogProps {
  /** The resolved job object. */
  job: GetScheduledJobsJobsItem;
  /**
   * Called when the user confirms. Pass extra params to merge into the run
   * request (e.g. `{ template_ids: [...] }` for SYNC_BOARD_TEMPLATES).
   * Omit or pass `{}` for jobs that need no additional input.
   */
  onConfirm: (extraParams?: Record<string, unknown>) => Promise<void>;
  /** Called when the user cancels. */
  onCancel: () => void;
  /** True while the job run request is in-flight. */
  isRunning: boolean;
}

export interface RunJobButtonProps {
  /** The job type to find and run. The job must exist for this org. */
  jobType: JobType;
  /** Button label. Defaults to 'Run Now'. */
  label?: string;
  /** Icon shown before the label. Pass null to suppress. Defaults to REFRESH. */
  icon?: IconName | null;
  variant?: 'default' | 'secondary' | 'outline' | 'destructive' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  /** Title/tooltip text on the button. */
  title?: string;
  /** Called after a successful run, with the run ID. */
  onComplete?: (runId: string) => void;
  /**
   * Optional dialog to show before running.
   *
   * When provided, clicking the button opens this dialog instead of running
   * immediately. The dialog is responsible for collecting any extra parameters
   * and calling `onConfirm(extraParams)` when ready. Use this for jobs that
   * need user input before execution (e.g. template selection, week picker).
   *
   * For jobs that need no input, omit this prop — the button runs immediately
   * on click.
   */
  renderDialog?: (props: RunJobDialogProps) => React.ReactNode;
}

/**
 * RunJobButton — a self-contained "run this job" button usable anywhere in the UI.
 *
 * Handles the full lifecycle:
 * - Resolves the job ID from the shared TQ scheduled-jobs cache (no extra request)
 * - Stays disabled while the jobs query is loading (fixes the race-condition bug)
 * - Renders nothing if the job doesn't exist for this org after load completes
 * - Calls RUN_SCHEDULED_JOB, updates the jobs cache, and shows a toast with a logs link
 *
 * For jobs requiring user input before execution (template selection, week picker, etc.),
 * pass a `renderDialog` render prop. Clicking the button opens the dialog; the button
 * handles execution once the dialog calls `onConfirm`.
 */
export function RunJobButton({
  jobType,
  label = 'Run Now',
  icon = IconName.REFRESH,
  variant = 'secondary',
  size = 'sm',
  title,
  onComplete,
  renderDialog,
}: RunJobButtonProps) {
  const toast = useToast();
  const { activeOrganizationId } = useActiveOrganization();
  const { data: jobsData, isLoading: isLoadingJobs } =
    useScheduledJobsQuery(activeOrganizationId);
  const { execute, isLoading: isRunning } = useOrchestrator<
    RunScheduledJobInput,
    RunScheduledJobOutput
  >(Action.RUN_SCHEDULED_JOB);

  const [dialogOpen, setDialogOpen] = useState(false);

  const job = jobsData?.jobs.find(j => j.job_type === jobType) ?? null;

  // After the jobs query resolves, if there's no matching job for this org, don't render.
  if (!isLoadingJobs && !job) return null;

  const runJob = async (extraParams: Record<string, unknown> = {}) => {
    if (!job) return;

    const result = await execute(
      { job_id: job.id, ...extraParams },
      { toast: false }
    );

    if (result.code === ResponseCode.SUCCESS && result.data) {
      const runId = result.data.run_id;

      // Keep the shared scheduled-jobs cache fresh
      queryClient.setQueryData<GetScheduledJobsOutput>(
        queryKeys.scheduledJobs.forOrg(job.organization_id),
        old =>
          old
            ? {
                ...old,
                jobs: old.jobs.map(j =>
                  j.id === job.id
                    ? {
                        ...j,
                        last_run_at: new Date().toISOString(),
                        last_run_status:
                          result.data?.status ?? JobRunStatus.COMPLETED,
                      }
                    : j
                ),
              }
            : old
      );

      if (runId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.jobRuns.forJob(job.id),
        });
      }

      const logsUrl = runId
        ? generateDeepLink(DeepLinkEntity.JOB_RUN, runId)
        : null;

      toast.success('Job started', {
        message: logsUrl ? 'Run is in progress.' : undefined,
        action: logsUrl
          ? {
              label: 'View Logs',
              onClick: () => window.open(logsUrl, '_blank'),
            }
          : undefined,
      });

      if (runId) onComplete?.(runId);
    } else {
      toast.error('Job failed to start', {
        message: result.code.replace(/_/g, ' ').toLowerCase(),
      });
    }
  };

  const handleClick = async () => {
    if (!job) return;
    if (renderDialog) {
      setDialogOpen(true);
    } else {
      await runJob();
    }
  };

  const handleDialogConfirm = async (
    extraParams: Record<string, unknown> = {}
  ) => {
    await runJob(extraParams);
    setDialogOpen(false);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const isLoading = isLoadingJobs || isRunning;

  return (
    <>
      <AsyncButton
        isLoading={isLoading}
        disabled={isLoading}
        onClick={handleClick}
        variant={variant}
        size={size}
        title={title}
      >
        {icon !== null && (
          <Icon
            name={isRunning ? IconName.REFRESH : icon}
            size="sm"
            className={`mr-1.5 ${isRunning ? 'animate-spin' : ''}`}
          />
        )}
        {isRunning ? `${label}...` : label}
      </AsyncButton>

      {renderDialog &&
        dialogOpen &&
        job &&
        renderDialog({
          job,
          onConfirm: handleDialogConfirm,
          onCancel: handleDialogCancel,
          isRunning,
        })}
    </>
  );
}
